class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    if (!this.hasAttribute('data-adding-to-cart')) this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  beginAdd(triggeredBy) {
    const wasOpen = this.classList.contains('active');
    this.setActiveElement(triggeredBy);
    this.setAttribute('data-adding-to-cart', '');
    this.setAttribute('data-shipping-pending', '');
    this.removeAttribute('data-shipping-timed-out');
    this.removeAttribute('data-cart-add-section-ready');
    clearTimeout(this.shippingPendingTimer);
    this.querySelector('.drawer__inner')?.setAttribute('aria-busy', 'true');
    let status = this.querySelector('[data-cart-add-status]');
    if (!status) {
      status = document.createElement('div');
      status.setAttribute('data-cart-add-status', '');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      this.querySelector('.drawer__inner')?.append(status);
    }
    status.textContent = 'Adding to bag…';
    document.dispatchEvent(new CustomEvent('bash:cart-add-pending'));
    this.open(triggeredBy);
    return wasOpen;
  }

  formatCartMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      const formatted = Shopify.formatMoney(cents, this.dataset.moneyFormat);
      const container = document.createElement('span');
      container.innerHTML = formatted;
      return container.textContent;
    }
    const currency = window.Shopify?.currency?.active || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency,
      }).format(cents / 100);
    } catch (_) {
      return `${currency} ${(cents / 100).toFixed(2)}`;
    }
  }

  showOptimisticItem(item, cartPromise) {
    this.shippingPendingTimer = setTimeout(() => {
      this.removeAttribute('data-shipping-pending');
      this.setAttribute('data-shipping-timed-out', '');
      document.dispatchEvent(new CustomEvent('bash:cart-add-shipping-timeout'));
    }, 8000);
    if (!item || !item.product_title || !this.hasAttribute('data-adding-to-cart')) return;
    const items = this.querySelector('cart-drawer-items');
    const contents = items?.querySelector('#CartDrawer-CartItems');
    if (!contents) return;
    const previousEmpty = this.classList.contains('is-empty');
    const total = this.querySelector('.totals__total-value');
    if (previousEmpty && total && Number.isFinite(Number(item.final_line_price))) {
      total.textContent = this.formatCartMoney(Number(item.final_line_price));
    }
    // The same cart snapshot is already needed for shipping and cart events.
    Promise.resolve(cartPromise).then((cart) => {
      const totalElement = this.querySelector('.totals__total-value');
      if (cart && totalElement && this.hasAttribute('data-adding-to-cart') && Number.isFinite(Number(cart.total_price))) {
        totalElement.textContent = this.formatCartMoney(cart.total_price);
      }
    }).catch(() => {});
    // Existing lines can have discounts and selling plans. Leave those prices to Liquid.
    const existing = [...contents.querySelectorAll('.quantity__input')].some((input) =>
      item.key ? input.dataset.quantityLineKey === item.key : input.dataset.quantityVariantId === String(item.variant_id)
    );
    if (existing) return;

    this.optimisticPreviousEmpty = previousEmpty;
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    items.classList.remove('is-empty');
    let tbody = contents.querySelector('.cart-items tbody');
    if (!tbody) {
      const wrapper = document.createElement('div');
      wrapper.className = 'drawer__cart-items-wrapper';
      const table = document.createElement('table');
      table.className = 'cart-items';
      table.setAttribute('aria-label', 'Bag items');
      tbody = table.createTBody();
      wrapper.append(table);
      contents.prepend(wrapper);
      this.optimisticWrapper = wrapper;
    }
    const row = tbody.insertRow();
    row.className = 'cart-item cart-item--optimistic';
    row.setAttribute('aria-busy', 'true');
    row.dataset.optimisticItem = '';
    this.setAttribute('data-optimistic-item', '');
    const media = row.insertCell();
    media.className = 'cart-item__media';
    if (item.featured_image?.url) {
      const image = document.createElement('img');
      try {
        const url = new URL(item.featured_image.url, window.location.origin);
        url.searchParams.set('width', '120');
        image.src = url.toString();
      } catch (_) { /* A missing image URL must not interrupt the completed add. */ }
      image.alt = item.featured_image.alt || item.product_title;
      image.width = 120;
      image.height = 120;
      image.className = 'cart-item__image';
      media.append(image);
    }
    const details = row.insertCell();
    details.className = 'cart-item__details';
    const title = document.createElement('span');
    title.className = 'cart-item__name h4 break';
    title.textContent = item.product_title;
    details.append(title);
    if (item.variant_title && item.variant_title !== 'Default Title') {
      const variant = document.createElement('div');
      variant.className = 'product-option';
      variant.textContent = item.variant_title;
      details.append(variant);
    }
    const quantity = document.createElement('div');
    quantity.className = 'product-option';
    quantity.textContent = `Qty: ${item.quantity}`;
    details.append(quantity);
    const totals = row.insertCell();
    totals.className = 'cart-item__totals right';
    const price = document.createElement('span');
    price.className = 'price price--end';
    const linePrice = Number(item.final_line_price ?? (item.final_price ?? item.price) * item.quantity);
    if (Number.isFinite(linePrice)) price.textContent = this.formatCartMoney(linePrice);
    totals.append(price);
    this.optimisticRow = row;

  }

  shippingSectionReady() {
    this.setAttribute('data-cart-add-section-ready', '');
    document.dispatchEvent(new CustomEvent('bash:cart-add-section-ready'));
  }

  finishShippingPending() {
    clearTimeout(this.shippingPendingTimer);
    this.removeAttribute('data-shipping-pending');
    this.removeAttribute('data-cart-add-section-ready');
  }

  endAdd(wasOpen, failed = false, message = '') {
    if (message) this.optimisticRow?.setAttribute('aria-busy', 'false');
    if (failed || !message) this.removeAttribute('data-optimistic-item');
    if (failed) {
      this.optimisticRow?.remove();
      this.optimisticWrapper?.remove();
      if (this.optimisticPreviousEmpty) {
        this.classList.add('is-empty');
        this.querySelector('.drawer__inner')?.classList.add('is-empty');
        this.querySelector('cart-drawer-items')?.classList.add('is-empty');
        const total = this.querySelector('.totals__total-value');
        if (total) total.textContent = this.formatCartMoney(0);
      }
      this.finishShippingPending();
      this.removeAttribute('data-shipping-timed-out');
      document.dispatchEvent(new CustomEvent('bash:cart-add-section-ready'));
    }
    this.optimisticRow = null;
    this.optimisticWrapper = null;
    this.optimisticPreviousEmpty = false;
    this.removeAttribute('data-adding-to-cart');
    this.querySelector('.drawer__inner')?.removeAttribute('aria-busy');
    const status = this.querySelector('[data-cart-add-status]');
    if (status && message) status.textContent = message;
    else status?.remove();
    if (failed && !wasOpen) this.close();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      if (typeof parsedState.sections?.[section.id] !== 'string') return;
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (this.classList.contains('active')) {
      trapFocus(document.getElementById('CartDrawer'), this.querySelector('.drawer__inner'));
      this.querySelector('cart-drawer-items')?.dispatchViewEvent();
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
